import React from 'react';

import { ReactComponent as NodeSVG } from '../../assets/images/stacks/nodedotjs.svg';
import { ReactComponent as PythonSVG } from '../../assets/images/stacks/python.svg';
import { ReactComponent as GoSVG } from '../../assets/images/stacks/go.svg';
import { ReactComponent as PHPSVG } from '../../assets/images/stacks/php.svg';
import { ReactComponent as RubySVG } from '../../assets/images/stacks/ruby.svg';
import { ReactComponent as JavaSVG } from '../../assets/images/stacks/openjdk.svg';
import { ReactComponent as ScalaSVG } from '../../assets/images/stacks/scala.svg';
import { ReactComponent as DotNetSVG } from '../../assets/images/stacks/dotnet.svg';
import { ReactComponent as HTMLSVG } from '../../assets/images/stacks/html5.svg';
import { ReactComponent as DockerSVG } from '../../assets/images/stacks/docker.svg';

// Official marks (Simple Icons, CC0). They ship with no `fill`, so the colour
// below is applied on the <svg> and inherited by the paths.
//
// The colour is the brand's own, EXCEPT where the brand's own is unreadable on
// this dashboard's navy (#0F1C32). Both exceptions are noted where they occur:
// an icon nobody can see is not on brand either.
type StackLogo = {
  Logo: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  color: string;
};

// Keys are the strings the API's `detectStack` returns — same spelling, so a
// new stack there shows up here the moment its logo is added.
const STACK_LOGOS: Record<string, StackLogo> = {
  'Node.js': { Logo: NodeSVG, color: '#5FA04E' },
  Python: { Logo: PythonSVG, color: '#3776AB' },
  Go: { Logo: GoSVG, color: '#00ADD8' },
  PHP: { Logo: PHPSVG, color: '#777BB4' },
  Ruby: { Logo: RubySVG, color: '#CC342D' },
  // OpenJDK's mark is pure black — invisible here. Java's other long-standing
  // colour (the one openjdk.org itself uses) reads fine.
  Java: { Logo: JavaSVG, color: '#E76F00' },
  Scala: { Logo: ScalaSVG, color: '#DC322F' },
  // #512BD4 on navy lands at 2.1:1. Lightened along the same hue to clear 3:1.
  '.NET': { Logo: DotNetSVG, color: '#7455DD' },
  Static: { Logo: HTMLSVG, color: '#E34F26' },
};

export const DOCKER_LOGO: StackLogo = { Logo: DockerSVG, color: '#2496ED' };

export const getStackLogo = (stack?: string): StackLogo | undefined =>
  stack ? STACK_LOGOS[stack] : undefined;

// The Buildpacks row's mark, detected stack or not — the stack's own logo
// goes beside the title instead. Deliberately geometric: three stacked layers,
// which is what a buildpack build is.
export const BuildpackLayersIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
    <path
      d="M12 3 3 7.5 12 12l9-4.5L12 3Z"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="m3 12 9 4.5L21 12"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m3 16.5 9 4.5 9-4.5"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// The stacks the builder detects, written for a sentence rather than for the
// map above: "static sites" reads as English where the `Static` key that
// `detectStack` returns does not. Shared so the empty state and the Buildpacks
// row can never drift into advertising different lists — they did, and the row
// was quietly missing two of them.
export const SUPPORTED_STACKS_SENTENCE =
  'Node.js, Python, Go, Java, Scala, PHP, Ruby, .NET and static sites';
